import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';
import { Progress } from 'antd';
import { Button, Label, Divider } from 'ui';
import { DrawerViewSelectedListingProducts } from 'ui/Drawer/Drawers/DrawerViewSelectedListingProducts';
import type {
  ListingApplication,
  Listing,
  ListingProduct,
  ApplicationProductData,
  SplitDeliveryData,
} from 'types/ListingData';
import type {
  ProductDataItem,
  SelectedProductItem,
  TreeTableRow,
} from '../types';
import { StyledApplicationsInfographics } from '../styles';

interface ViewApplicationRecord {
  id: number | undefined;
  status: string | undefined;
  delivery_cost: number | undefined;
  total_offer_price: number | string | null | undefined;
  order_transaction:
    | { id: number; parent_transaction_id?: number | null }
    | null
    | undefined;
  openProductsTab: boolean;
}

interface ApplicationsInfographicsProps {
  applications: ListingApplication[];
  listing: Listing | null;
  selectedProducts?: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  savedSelectedProducts?: Array<{
    listing_application_id: number;
    listing_application_product_id: number;
    selected_quantity: number;
  }>;
  splitDeliveries?: SplitDeliveryData[];
  isSingleProducerOnly?: boolean;
  onViewApplication?: (
    application:
      | ListingApplication
      | TreeTableRow
      | ViewApplicationRecord
      | null
  ) => void;
  selectProducts?: (data: {
    listing_id: string;
    selections: Array<{
      application_id: number;
      listing_application_product_id: number;
      selected_quantity: number;
    }>;
  }) => void;
  onCreateTransactions?: (data: { listing_id: string }) => Promise<void>;
}

export const ApplicationsInfographics = ({
  applications,
  listing,
  selectedProducts,
  savedSelectedProducts,
  splitDeliveries,
  isSingleProducerOnly,
  onViewApplication,
  selectProducts,
  onCreateTransactions,
}: ApplicationsInfographicsProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const productsData: ProductDataItem[] = useMemo(() => {
    if (!listing?.products) return [];

    return listing.products.map((product: ListingProduct) => {
      let selectedProductItems: Array<{
        product: ApplicationProductData;
        application: ListingApplication;
      }> = [];

      if (isSingleProducerOnly) {
        selectedProductItems = applications.flatMap(
          (app) =>
            app.application_products
              ?.filter(
                (appProd: ApplicationProductData) =>
                  appProd.listing_product_id === product.id
              )
              .map((appProd) => ({ product: appProd, application: app })) || []
        );
      } else {
        selectedProductItems = applications.flatMap(
          (app) =>
            app.application_products
              ?.filter((appProd: ApplicationProductData) => {
                const isSelected = selectedProducts?.some(
                  (selected) =>
                    selected.listing_application_id === app.id &&
                    selected.listing_application_product_id === appProd.id
                );
                const matchesCategory =
                  appProd.listing_product_id === product.id;
                return matchesCategory && isSelected;
              })
              .map((appProd) => ({ product: appProd, application: app })) || []
        );
      }

      const selectedQuantity = selectedProductItems.reduce(
        (sum: number, item) => {
          const { product: appProd, application: app } = item;

          const selectedData = selectedProducts?.find(
            (s) =>
              s.listing_application_id === app.id &&
              s.listing_application_product_id === appProd.id
          );

          const quantity = selectedData
            ? selectedData.selected_quantity
            : appProd.offered_quantity !== null &&
                appProd.offered_quantity !== undefined
              ? appProd.offered_quantity
              : (() => {
                  const listingProduct = listing.products?.find(
                    (lp) => lp.id === appProd.listing_product_id
                  );
                  const requiredQty = listingProduct?.required_quantity;
                  return typeof requiredQty === 'string'
                    ? parseFloat(requiredQty) || 0
                    : requiredQty || 0;
                })();

          return sum + quantity;
        },
        0
      );

      const requiredQuantity = Number(product.required_quantity) || 0;
      const percent =
        requiredQuantity > 0 ? (selectedQuantity / requiredQuantity) * 100 : 0;

      const selectedProductsList: SelectedProductItem[] = selectedProductItems
        .map((item) => {
          const { product: appProd, application: app } = item;

          const selectedData = selectedProducts?.find(
            (s) =>
              s.listing_application_id === app.id &&
              s.listing_application_product_id === appProd.id
          );

          const quantity = selectedData
            ? selectedData.selected_quantity
            : appProd.offered_quantity !== null &&
                appProd.offered_quantity !== undefined
              ? appProd.offered_quantity
              : (() => {
                  const listingProduct = listing.products?.find(
                    (lp) => lp.id === appProd.listing_product_id
                  );
                  const requiredQty = listingProduct?.required_quantity;
                  return typeof requiredQty === 'string'
                    ? parseFloat(requiredQty) || 0
                    : requiredQty || 0;
                })();

          return {
            company_name: app.farm_brand || app.farm_name || '',
            quantity: quantity,
            storage_unit: product.storage_unit?.short || '',
            delivery_cost: app.delivery_cost || 0,
          };
        })
        .filter((item: SelectedProductItem) => item.quantity > 0);

      const result = {
        category_name: product.name || product.category?.name || '',
        required_quantity: requiredQuantity,
        selected_quantity: selectedQuantity,
        percent,
        selected_products: selectedProductsList,
        storage_unit: product.storage_unit?.short || '',
      };

      return result;
    });
  }, [applications, listing, selectedProducts, isSingleProducerOnly]);

  return (
    <StyledApplicationsInfographics>
      <Label
        label={intl.formatMessage({
          id: 'listings.applications.infographics.title',
        })}
        title={true}
        className="infographics-title"
      />
      <Divider thickness={3} />
      {productsData.map((product: ProductDataItem, index: number) => (
        <div key={index}>
          <div className="product-item">
            <div className="product-row">
              <div className="product-header">
                <Label label={product.category_name} extraBold={true} />
                <Label
                  label={`${intl.formatMessage({
                    id: 'listings.applications.table.quantity',
                  })}: ${product.required_quantity} ${product.storage_unit}.`}
                />
              </div>
              <div className="progress-container">
                <Progress
                  type="circle"
                  size={46}
                  strokeWidth={4}
                  percent={product.percent}
                  strokeColor={theme.colorText}
                  format={(percent) => `${Math.round(product.percent)}%`}
                />
              </div>
            </div>
            <div className="selected-products">
              {product.selected_products.map(
                (selected: SelectedProductItem, idx: number) => (
                  <div key={`${index}-${idx}`}>
                    {selected.company_name} - {selected.quantity}{' '}
                    {selected.storage_unit}. (Piegāde: {selected.delivery_cost})
                  </div>
                )
              )}
            </div>
          </div>
          {index < productsData.length - 1 && <Divider />}
        </div>
      ))}
      <div className="buttons">
        <Button
          type="primary"
          label={intl.formatMessage({
            id: 'listings.applications.infographics.view',
          })}
          onClick={() => setDrawerOpen(true)}
        />
      </div>
      <DrawerViewSelectedListingProducts
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        closable={true}
        selectedProducts={selectedProducts ?? []}
        savedSelectedProducts={savedSelectedProducts ?? []}
        applications={applications}
        listing={listing}
        existingSplitDeliveries={splitDeliveries}
        onViewApplication={onViewApplication}
        selectProducts={selectProducts}
        onCreateTransactions={onCreateTransactions}
      />
    </StyledApplicationsInfographics>
  );
};
